/**
 * Pure helpers for the PO detail dates section: the date-input round trip and
 * the revision summary line shared by the Ready ETA and Arrival ETA rows.
 *
 * PR #1989 review: built on date-fns + lib/format-dates (DESIGN.md §17) so the
 * panel prints dates in the same shape and locale as the rest of the app. The
 * general parser and formatters live in lib/format-dates.
 */
import { differenceInCalendarDays, format, isValid, parseISO, setHours } from "date-fns";
import type { Locale } from "date-fns/locale";
import type { OrderDateHistoryEntry } from "@threadheads/shared";

import { DATE_INPUT_FORMAT, formatDay, parseDateValue } from "@/lib/format-dates";

/** One ETA revision, straight from `purchaseOrders.getOrderDateHistory`. */
export type EtaRevision = OrderDateHistoryEntry;

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** `yyyy-MM-dd` from LOCAL date components, for `<input type="date">`. */
export function toDateInputValue(value: string | null | undefined): string {
  const date = parseDateValue(value);
  return date ? format(date, DATE_INPUT_FORMAT) : "";
}

/** Parse a date-input value: exactly `YYYY-MM-DD` and a real day, else null. */
function parseDateInput(day: string): Date | null {
  // parseISO alone is lenient (it accepts `2026-07-01T00:00`), so the shape
  // check runs first; isValid then rejects impossible days like 2026-02-30.
  if (!CALENDAR_DAY.test(day)) return null;
  const date = parseISO(day);
  return isValid(date) ? date : null;
}

/** Wire value for a calendar (DATE) field: exactly `YYYY-MM-DD`, else null. */
export function dateInputToCalendarDay(day: string): string | null {
  return parseDateInput(day) ? day : null;
}

/**
 * Wire value for an instant (TIMESTAMPTZ) field: the user's LOCAL noon on the
 * picked day as an ISO instant. Keeps the calendar day in the user's zone and
 * every zone within 12 hours of it. Null for malformed input.
 */
export function dateInputToLocalNoonIso(day: string): string | null {
  const date = parseDateInput(day);
  return date ? setHours(date, 12).toISOString() : null;
}

/** Whole calendar days from `from` to `to` (local days), or null when either side is missing. */
export function dayDelta(from: string | null | undefined, to: string | null | undefined): number | null {
  const a = parseDateValue(from);
  const b = parseDateValue(to);
  return a && b ? differenceInCalendarDays(b, a) : null;
}

function formatDelta(days: number): string {
  const sign = days > 0 ? "+" : days < 0 ? "-" : "";
  const abs = Math.abs(days);
  return `${sign}${abs} ${abs === 1 ? "day" : "days"}`;
}

/**
 * `Revised once · first 19 Jun 2026 (+12 days)` / `Revised 3× · first ...`.
 *
 * PR #1989 review: only an entry that replaced an existing date (`old` set)
 * is a revision. Setting the date the first time is not, so a PO whose ETA
 * was only ever entered once shows no summary at all.
 *
 * `first` is the earliest entry's old value (its new value when old is null,
 * i.e. the first time the date was set); the delta runs from `first` to the
 * current ETA. Null when nothing was revised.
 */
export function summarizeEtaHistory(
  revisions: EtaRevision[],
  currentEta: string | null | undefined,
  locale: Locale
): string | null {
  const sorted = [...revisions].sort((x, y) => x.at.localeCompare(y.at));
  const earliest = sorted[0];
  const revised = sorted.filter((entry) => entry.old !== null).length;
  if (!earliest || revised === 0) return null;
  const first = earliest.old ?? earliest.new;
  const count = revised === 1 ? "Revised once" : `Revised ${revised}×`;
  const firstPart = first ? ` · first ${formatDay(first, locale)}` : "";
  const delta = dayDelta(first, currentEta);
  const deltaPart = first && delta !== null ? ` (${formatDelta(delta)})` : "";
  return `${count}${firstPart}${deltaPart}`;
}

/** One popover line: `Set to X`, `X → Y`, or `Cleared (was X)`. */
export function describeRevision(revision: EtaRevision, locale: Locale): string {
  if (revision.old === null) {
    return revision.new === null ? "Cleared" : `Set to ${formatDay(revision.new, locale)}`;
  }
  if (revision.new === null) return `Cleared (was ${formatDay(revision.old, locale)})`;
  return `${formatDay(revision.old, locale)} → ${formatDay(revision.new, locale)}`;
}

/** Newest first, for the popover list. */
export function revisionsNewestFirst(revisions: EtaRevision[]): EtaRevision[] {
  return [...revisions].sort((x, y) => y.at.localeCompare(x.at));
}
